import { CSSProperties, useMemo, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Container, Form, Input, Message, Schema, SelectPicker, Stack, Uploader } from 'rsuite'
import { defaultLookupName } from '@shared/lookupTable'
import ProgressLine from 'rsuite/esm/Progress/ProgressLine'
import { FileType } from 'rsuite/esm/Uploader'
import Loading, { EnumLoadingType } from '@/client/components/Loading'
import useQuery from '@client/utils/useQuery'
import useProgress from '@/client/utils/useProgress'
import ModalDialog from '@client/components/ModalDialog'
import { Table } from '@client/components/Controls'
import { TOutTableColumn } from '@client/models/outTableColumn'
import InDataList from '@client/models/inDataList'
import project from '@client/stores/projectStore'
import toastMessage from '@/client/utils/toastMessage'
import OutTableColumnModel from '@/client/models/outTableColumn'

type TTemplateFormData = any

type UpdateTemplateProps = {
    db: string
    id: string
    type: string
    setOpenDialog: (openDialog: boolean) => void
    onUpload: (uploadedFile: any) => void
}
type TableSelectorProps = { index: number; name: string; value: string }
type ClientProps = { value: string }

const UploadContentStyle = {
    minHeight: 36,
    display: 'flex',
    alignItems: 'center',
    padding: '0.75em 0.5em',
} as CSSProperties
const UploadButtonStyle = {
    textOverflow: 'ellipsis',
    overflow: 'hidden',
} as CSSProperties
const selectTableStyle = {
    flex: 1,
} as CSSProperties
const clientContainerStyle = {
    display: 'flex',
    flexFlow: 'row nowrap',
    alignItems: 'center',
    marginBottom: '1em',
    gap: 5,
} as CSSProperties
const selectClientStyle = {} as CSSProperties
const TabRowStyle = {
    display: 'flex',
    alignItems: 'center',
    gap: '1em',
} as CSSProperties
const LabelStyle = {
    width: '3em',
} as CSSProperties
const deleteWrapperStyle = {
    display: 'flex',
    width: '100%',
    alignItems: 'center',
    height: '2em',
    marginLeft: '3em',
    marginBottom: '1em',
} as CSSProperties
const deleteCheckboStyle = {
    width: 'auto',
    marginRight: '1em',
} as CSSProperties

const inTemplate = new InDataList()
const outTableColumn = new OutTableColumnModel()

const { StringType, BooleanType } = Schema.Types

export default function UpdateTemplate({ db, id, type, setOpenDialog, onUpload }: UpdateTemplateProps) {
    const { t } = useTranslation()
    const newRecord = type == 'add'
    const formRef = useRef<TTemplateFormData>(undefined)
    const clientRef = useRef<any>(undefined)
    const clearRef = useRef<any>(undefined)
    const [modified, setModified] = useState<number>(0)
    const [uploading, setUploading] = useState<boolean>(false)
    const [uploadMessage, setUploadMessage] = useState<string>('')
    const [taskId, setTaskId] = useState<string>('')
    const {
        data: outTables,
        loading: outTableLoading,
        error: outTableError,
    } = useQuery(outTableColumn.list, { args: { project: db, mapping: false, page: 1, count: 200 } })
    const { task, finished, failed, error } = useProgress(taskId)
    const [formData, setFormData] = useState<TTemplateFormData>((current: any) => {
        if (current) return current
        return null
    })
    const currentProject = project.currentProject.value
    const getClientList = function () {
        if (!currentProject) return []
        return currentProject?.clients.map((item: string) => ({ label: item, value: item }))
    }

    const availableTemplates = useMemo(() => {
        if (!outTables || outTables.length == 0) return []
        return outTables.map((item: TOutTableColumn) => {
            return { value: item._id, label: `${item.name} (${item._id})` }
        })
    }, [outTables])

    const buttons = [
        { status: 'save', label: t('Save'), disabled: modified == 0 },
        { status: 'cancel', label: t('Cancel') },
    ]

    // async function projectNameExists  (id: string, projectName: string, newRecord: boolean) {
    //     let retCode = false
    //     if (!newRecord || projectName) {
    //         const result = await template.getProjectByName(projectName, id)
    //         if (result?.data) retCode = true
    //     }
    //     return retCode
    // }

    // A checked file waits on the server until it is saved. Not saved, it goes
    // — it holds the data of the old system (B9, operator 16.10.2026).
    // read here: the upload handler has a FormData of the same name
    const pendingUpload = (): string => formData?.uploadFile || ''
    function discardUpload(fileName = pendingUpload()) {
        if (fileName) void inTemplate.cancelUpload(db, fileName)
    }

    function handleDialogButtons(status: string) {
        if (status === 'cancel') {
            discardUpload()
            setOpenDialog(false)
            return true
        }

        if (status === 'save') {
            if (modified) {
                onApplyClick()
            }
            return false
        }

        // removeTab(modifiedDialog?.key)
        discardUpload()
        setOpenDialog(false)
        return true
    }

    async function handleOnChangeUpload(fileList: FileType[]) {
        if (fileList.length == 0) return

        setUploading(true)
        setUploadMessage(t('Analysing data list ...'))

        const file: any = fileList[fileList.length - 1]
        console.log('Database name: ', db)
        console.log('File to upload: ', file)

        let formData = new FormData()
        formData.append('template', file.blobFile)

        const result: any = await inTemplate.checkUpload(db, formData, {
            headers: {
                'Content-Type': 'multipart/form-data',
            },
            data: {
                header: 0,
            },
            //onUploadProgress,
        })
        console.log('check result: ', result?.data)

        const upload = result?.data
        // another file in place of the one checked before
        if (pendingUpload() && pendingUpload() !== upload?.fileName) discardUpload()
        const sheets = upload?.sheets || []
        for (let i = 0; i < sheets.length; i++) {
            sheets[i].index = i
            sheets[i].table = ''
            // without a template a sheet becomes a lookup table of this name
            sheets[i].name = defaultLookupName(sheets[i].sheetName)
        }

        const newFormData = {
            ...formData,
            ...{
                uploadFile: upload?.fileName || '',
                fileName: file.name,
                sheets: sheets,
            },
        }
        setFormData(newFormData)
        setModified((modified) => modified + 1)

        setUploading(false)
        setUploadMessage(t(''))
    }

    function onApplyClick() {
        const saveData = { ...formData }
        const id = saveData._id || ''
        saveData.clear = clearRef.current?.checked || true
        saveData.client = clientRef.current?.target?.outerText || ''
        if (saveData.client.includes('...')) {
            if (currentProject && currentProject.clients.length <= 1) {
                saveData.client = ''
            } else {
                alert(t('You have to select a client!'))
                return
            }
        }

        formRef.current.checkAsync().then(async (result: any) => {
            if (!result.hasError) {
                setUploading(true)
                setUploadMessage(t('Initializing ...'))
                const result =
                    newRecord || !id ? await inTemplate.create(db, saveData) : await inTemplate.update(db, id, saveData)
                console.log('upload result: ', result)
                if (!result) {
                    setOpenDialog(false)
                    setUploading(false)
                    return
                } else {
                    onUpload(result)
                    toastMessage(t('Successfully saved'))
                    setOpenDialog(false)
                    setUploading(false)
                }

                setTaskId(result.data.taskId)
                setUploadMessage('')
            }
        })
    }

    const UploadFile = () => {
        let style = { ...UploadContentStyle }
        if (!formData?.fileName) {
            style.textAlign = 'center'
            style.justifyContent = 'center'
        }

        return (
            <Uploader
                name="template"
                action="/templates/in/check"
                accept=".xls,.xlsx,.xml,.csv"
                autoFocus={true}
                multiple={false}
                autoUpload={false}
                withCredentials={false}
                fileListVisible={false}
                onChange={handleOnChangeUpload}
                draggable
            >
                <div style={style}>
                    <span style={UploadButtonStyle}>
                        {formData?.fileName ? formData.fileName : t('Click or Drag file to this area to upload.')}
                    </span>
                </div>
            </Uploader>
        )
    }

    const Task = () => {
        const progress = task?.progress || 0
        const message = task?.message || t('Initialize ...')

        return (
            <>
                <ProgressLine percent={progress} />
                <span>{message}</span>
            </>
        )
    }

    const TableSelector = ({ index, name, value }: TableSelectorProps) => {
        if (outTableError) return <span>{outTableError.message}</span>
        if (outTableLoading) return <span>{t('Loading ...')}</span>

        function handleSelectTable(index: number, name: string, value: any): void {
            const newFormData = { ...formData }
            newFormData.sheets[index].table = value
            setFormData(newFormData)
        }

        return (
            <SelectPicker
                style={selectTableStyle}
                //ref={tableRef}
                defaultValue={value}
                data={availableTemplates}
                // cleared again, the sheet goes back to being a lookup table
                cleanable={true}
                searchable={false}
                placeholder={t('Select table ...')}
                onSelect={(value) => {
                    handleSelectTable(index, name, value)
                }}
                onClean={() => {
                    handleSelectTable(index, name, '')
                }}
            />
        )
    }

    // Columns without a header are imported under their number (operator,
    // 16.10.2026); the check says which, so nobody looks for them later.
    const UnnamedColumns = () => {
        const sheets = (formData?.sheets ?? []).filter((sheet: any) => sheet.unnamed?.length)
        if (!sheets.length) return null
        return (
            <Message type="warning" showIcon className="upload-unnamed-columns">
                {sheets.map((sheet: any) => (
                    <div key={sheet.sheetName}>
                        {sheet.sheetName}: {t('Columns without a header: {{columns}}. They are named after their number.', {
                            columns: sheet.unnamed.join(', '),
                        })}
                    </div>
                ))}
            </Message>
        )
    }

    const Sheets = () => {
        return (
            <Table
                className="upload-in-table"
                //(loading={loading}
                wordWrap="break-word"
                //fillHeight
                //autoHeight
                showHeader
                data={formData?.sheets}
                renderEmpty={renderEmpty}
            >
                <Table.Column align="left" flexGrow={1}>
                    <Table.HeaderCell>{t('Template')}</Table.HeaderCell>
                    <Table.Cell dataKey="table">
                        {(row: any) => {
                            return <TableSelector value={row.table} name={row.sheetName} index={row.index} />
                        }}
                    </Table.Cell>
                </Table.Column>
                <Table.Column align="left" verticalAlign="center" flexGrow={1}>
                    <Table.HeaderCell>{t('Sheet name')}</Table.HeaderCell>
                    <Table.Cell dataKey="sheetName" />
                </Table.Column>
                <Table.Column align="left" verticalAlign="center" flexGrow={1}>
                    <Table.HeaderCell>{t('Lookup table')}</Table.HeaderCell>
                    <Table.Cell dataKey="name">
                        {(row: any) =>
                            row.table ? null : (
                                // uncontrolled: the table is drawn anew on every change,
                                // a controlled field would lose the cursor at each key
                                <Input
                                    name={`lookup-name-${row.index}`}
                                    size="sm"
                                    defaultValue={row.name}
                                    title={t('An upload under the same name replaces the table.')}
                                    onChange={(value) => {
                                        formData.sheets[row.index].name = value
                                    }}
                                />
                            )
                        }
                    </Table.Cell>
                </Table.Column>
                <Table.Column align="left" verticalAlign="center" width={75}>
                    <Table.HeaderCell>{t('Columns')}</Table.HeaderCell>
                    <Table.Cell dataKey="columns" />
                </Table.Column>
                <Table.Column align="left" verticalAlign="center" width={100}>
                    <Table.HeaderCell>{t('Rows')}</Table.HeaderCell>
                    <Table.Cell dataKey="rows" />
                </Table.Column>
            </Table>
        )
    }

    const Clients = ({ value }: ClientProps) => {
        if (!currentProject?.clients.length) return null

        const handleClientChanged = (value: string): void => {
            const newFormData = { ...formData, ...{ client: value } }
            //setFormData(newFormData)
        }

        return (
            <Container style={clientContainerStyle}>
                <label style={LabelStyle}>{t('Client')}</label>
                <SelectPicker
                    style={selectClientStyle}
                    ref={clientRef}
                    defaultValue={value}
                    data={getClientList()}
                    placeholder={currentProject?.clients.length ? t('Select client ...') : t('No clients available.')}
                    cleanable={false}
                    searchable={false}
                    sort={() => {
                        return (a, b) => {
                            return a.value.localeCompare(b.value)
                        }
                    }}
                    /* @ts-ignore */
                    onSelect={(value) => {
                        handleClientChanged(value)
                    }}
                />
            </Container>
        )
    }

    const model = Schema.Model({
        client: StringType(),
        clear: BooleanType(),
    })

    const formComponent = useMemo(
        () => (
            <Form fluid layout='horizontal' className="form-distance" ref={formRef} formValue={formData} model={model} nestedField>
                <Stack>
                    <Clients value={clientRef?.current?.target?.outerText || ''} />
                    {/* <div style={deleteWrapperStyle} >
                        <input type="checkbox" id="deleteCheckbox" name="clear" ref={clearRef} style={deleteCheckboStyle} />
                        <label htmlFor="deleteCheckbox">{t('Delete before upload')}</label>
                    </div> */}
                </Stack>

                <UploadFile />

                <Sheets />

                <UnnamedColumns />

                {/* <div style={FooterStyle}>
                    {hasErrors() && <span style={FooterErrorWrapperStyle}><span style={FooterErrorStyle}>{t('Check your inputs')}</span></span>}
                </div> */}
            </Form>
        ),
        [formData]
    )

    if (taskId && finished) {
        setTimeout(() => {
            // An import that broke used to leave the bar standing where it
            // stopped, with nothing said. Now it says so and closes like any
            // other end.
            if (failed) toastMessage(`${t('Import failed')}: ${error}`, 'error')

            setOpenDialog(false)
            setUploading(false)
            setUploadMessage('')
            setTaskId('')
        }, 0)
    }

    const renderEmpty = () => {
        return <div className="rs-table-body-info">{t('No sheet data found.')}</div>
    }

    return (
        <ModalDialog
            title={t(newRecord ? 'New data list' : 'Edit data list')}
            buttons={buttons}
            onClick={handleDialogButtons}
            size="md"
        //autoScroll={false}
        >
            {formComponent}

            {uploading && (
                <Loading type={EnumLoadingType.Spinner} overlay={false}>
                    {taskId != '' && <Task />}
                    {uploadMessage}
                </Loading>
            )}
            {/* <JSONView formValue={formData} formError={formError} /> */}
        </ModalDialog>
    )
}
